import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { SITE_ID } from '../lib/constants';
import { mockProducts } from '../mocks/products';

export interface Product {
  id: string;
  name: string;
  short_description: string;
  aciklama: string;
  price: number;
  original_price: number | null;
  stock: number;
  category_name: string;
  image_url: string;
  galeri_gorseller: string[];
  slug: string;
  weight: string | null;
  badge: string | null;
  rating: number;
  review_count: number;
  ozellikler: Record<string, string> | null;
  para_birimi: string;
  seo_baslik: string | null;
  seo_aciklama: string | null;
  ana_urun_id: string | null;
  varyantlar?: ProductVariant[];
  ana_urun?: Product | null; // Ana ürün bilgisi (varyasyonlu ürünler için)
  diger_varyasyonlar?: Product[]; // Aynı ana ürüne bağlı diğer varyasyonlar
}

export interface ProductVariant {
  id: string;
  varyant_adi: string;
  ozellikler: Record<string, string>;
  fiyat: number | null;
  stok_miktari: number;
  sku: string | null;
  gorsel_url: string | null;
  urun_slug?: string | null; // Bu varyanta karşılık gelen ayrı ürünün slug'ı (varsa)
}

function mapDbProduct(p: Record<string, unknown>): Product {
  const fiyat = Number(p.fiyat) || 0;
  const indirimli = p.indirimli_fiyat ? Number(p.indirimli_fiyat) : null;
  const agirlik = p.agirlik ? `${p.agirlik} kg` : null;
  const galeri = Array.isArray(p.galeri_gorseller) ? (p.galeri_gorseller as string[]) : [];

  return {
    id: p.id as string,
    name: p.urun_adi as string,
    short_description: (p.kisa_aciklama as string) || '',
    aciklama: (p.aciklama as string) || '',
    price: indirimli !== null ? indirimli : fiyat,
    original_price: indirimli !== null ? fiyat : null,
    stock: (p.stok_miktari as number) || 0,
    category_name: (p.kategori as string) || '',
    image_url: (p.gorsel_url as string) || '',
    galeri_gorseller: galeri,
    slug: (p.seo_url as string) || (p.id as string),
    weight: agirlik,
    badge: indirimli !== null ? 'İndirim' : null,
    rating: 4.5,
    review_count: 0,
    ozellikler: (p.ozellikler as Record<string, string>) || null,
    para_birimi: (p.para_birimi as string) || 'TRY',
    seo_baslik: (p.seo_baslik as string) || null,
    seo_aciklama: (p.seo_aciklama as string) || null,
    ana_urun_id: (p.ana_urun_id as string) || null,
  };
}

export function useProducts(categoryName?: string) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchProducts() {
      setLoading(true);
      setError(null);
      try {
        let query = supabase
          .from('urunler')
          .select('*')
          .eq('site_id', SITE_ID)
          .eq('aktif', true)
          .order('olusturma_tarihi', { ascending: false });

        if (categoryName) {
          query = query.eq('kategori', categoryName);
        }

        const { data, error: err } = await query;
        if (cancelled) return;
        if (err) throw err;

        if (data && data.length > 0) {
          setProducts(data.map(mapDbProduct));
        } else {
          // Fallback to mock data if no DB data yet
          const mocked = mockProducts.map(p => ({
            ...p,
            short_description: p.short_description || '',
            aciklama: p.short_description || '',
            stock: 99,
            galeri_gorseller: [],
            ozellikler: null,
            para_birimi: 'TRY',
            seo_baslik: null,
            seo_aciklama: null,
            ana_urun_id: null,
          } as Product));
          setProducts(categoryName ? mocked.filter(p => p.category_name === categoryName) : mocked);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Ürünler yüklenemedi');
          const mocked = mockProducts.map(p => ({
            ...p,
            short_description: p.short_description || '',
            aciklama: p.short_description || '',
            stock: 99,
            galeri_gorseller: [],
            ozellikler: null,
            para_birimi: 'TRY',
            seo_baslik: null,
            seo_aciklama: null,
            ana_urun_id: null,
          } as Product));
          setProducts(mocked);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchProducts();
    return () => { cancelled = true; };
  }, [categoryName]);

  return { products, loading, error };
}

export function useProduct(slug: string) {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchProduct() {
      setLoading(true);
      setError(null);
      try {
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);

        let query = supabase
          .from('urunler')
          .select('*')
          .eq('site_id', SITE_ID)
          .eq('aktif', true);

        if (isUUID) {
          query = query.or(`seo_url.eq.${slug},id.eq.${slug}`);
        } else {
          query = query.eq('seo_url', slug);
        }

        const { data, error: err } = await query.maybeSingle();

        if (cancelled) return;
        if (err) throw err;

        if (data) {
          const mapped = mapDbProduct(data as Record<string, unknown>);

          // Ana ürün ID'sini belirle (bu ürün ana ürünse kendi ID'si, varyasyonsa ana_urun_id)
          const anaUrunId = mapped.ana_urun_id || mapped.id;

          // Ana ürünün varyantlarını çek
          const { data: varData } = await supabase
            .from('urun_varyantlari')
            .select('id, varyant_adi, ozellikler, fiyat, stok_miktari, sku, gorsel_url')
            .eq('urun_id', anaUrunId)
            .eq('aktif', true)
            .order('olusturma_tarihi', { ascending: true });

          // Aynı ana ürüne bağlı tüm varyasyonlu ürünleri çek (slug eşleştirmesi için)
          const { data: varyasyonUrunler } = await supabase
            .from('urunler')
            .select('id, seo_url, ozellikler, urun_adi')
            .eq('ana_urun_id', anaUrunId)
            .eq('site_id', SITE_ID)
            .eq('aktif', true);

          // Ana ürünün kendi ozelliklerini de ekle (ana ürün de bir varyasyon sayılır)
          const { data: anaUrunData } = await supabase
            .from('urunler')
            .select('*')
            .eq('id', anaUrunId)
            .eq('site_id', SITE_ID)
            .eq('aktif', true)
            .maybeSingle();

          const anaUrun: Product | null = anaUrunData ? mapDbProduct(anaUrunData as Record<string, unknown>) : null;

          // Gerçek rating ve yorum sayısını çek
          // Varyasyonlu ürünse ana ürünün yorumlarını da dahil et
          const urunIdleri = [mapped.id];
          if (mapped.ana_urun_id) urunIdleri.push(mapped.ana_urun_id);
          (varyasyonUrunler || []).forEach((u: Record<string, unknown>) => {
            if (u.id && !urunIdleri.includes(u.id as string)) {
              urunIdleri.push(u.id as string);
            }
          });

          const { data: yorumData } = await supabase
            .from('urun_yorumlari')
            .select('puan')
            .in('urun_id', urunIdleri)
            .eq('site_id', SITE_ID)
            .eq('onaylandi', true);

          const yorumlar = yorumData || [];
          const yorumSayisi = yorumlar.length;
          const ortalamaPuan = yorumSayisi > 0
            ? Math.round((yorumlar.reduce((sum, y) => sum + (y.puan || 0), 0) / yorumSayisi) * 10) / 10
            : 0;

          mapped.rating = ortalamaPuan;
          mapped.review_count = yorumSayisi;

          if (!cancelled) {
            // varyasyonMap'e urun_adi de ekleyelim (daha kesin eşleşme için)
            const varyasyonMapWithName: Array<{ ozellikler: Record<string, string>; slug: string; urun_adi: string }> = [];
            
            if (anaUrunData) {
              varyasyonMapWithName.push({
                ozellikler: (anaUrunData.ozellikler as Record<string, string>) || {},
                slug: anaUrunData.seo_url as string,
                urun_adi: (anaUrunData.urun_adi as string) || '',
              });
            }
            (varyasyonUrunler || []).forEach((u: Record<string, unknown>) => {
              varyasyonMapWithName.push({
                ozellikler: (u.ozellikler as Record<string, string>) || {},
                slug: u.seo_url as string,
                urun_adi: (u.urun_adi as string) || '',
              });
            });

            const mevcutVaryantlar = (varData || []).map((v: Record<string, unknown>) => {
              const vOzellikler = (v.ozellikler as Record<string, string>) || {};
              const varyantAdi = (v.varyant_adi as string) || '';
              
              // Eşleşme: varyant adı ürün adında tam olarak geçiyor mu?
              // Örn: varyant_adi="500 gr" → "Cremacaffe - 500 gr" ürünüyle eşleşir
              // Önce urun_adi üzerinden eşleştir (daha güvenilir)
              let eslesenUrun = varyasyonMapWithName.find(u => {
                const urunAdiLower = u.urun_adi.toLowerCase();
                const vAdLower = varyantAdi.toLowerCase();
                return urunAdiLower.includes(vAdLower);
              });

              // urun_adi eşleşmezse slug üzerinden dene
              if (!eslesenUrun) {
                eslesenUrun = varyasyonMapWithName.find(u => {
                  const normalizedVaryantAdi = varyantAdi.toLowerCase()
                    .replace(/\s+/g, '-')
                    .replace(/[()]/g, '')
                    .replace(/--+/g, '-');
                  return u.slug.toLowerCase().includes(normalizedVaryantAdi);
                });
              }

              return {
                id: v.id as string,
                varyant_adi: varyantAdi,
                ozellikler: vOzellikler,
                fiyat: v.fiyat ? Number(v.fiyat) : null,
                stok_miktari: (v.stok_miktari as number) || 0,
                sku: (v.sku as string) || null,
                gorsel_url: (v.gorsel_url as string) || null,
                urun_slug: eslesenUrun ? eslesenUrun.slug : null,
              };
            });

            mapped.varyantlar = mevcutVaryantlar;
            mapped.ana_urun = anaUrun;
            mapped.diger_varyasyonlar = [];
            setProduct(mapped);
          }
        } else {
          // Fallback to mock
          const mock = mockProducts.find(p => p.slug === slug || p.id === slug);
          if (mock) {
            setProduct({
              ...mock,
              short_description: mock.short_description || '',
              aciklama: mock.short_description || '',
              stock: 99,
              galeri_gorseller: [],
              ozellikler: null,
              para_birimi: 'TRY',
              seo_baslik: null,
              seo_aciklama: null,
              ana_urun_id: null,
              varyantlar: [],
            } as Product);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError('Ürün yüklenemedi');
          const mock = mockProducts.find(p => p.slug === slug || p.id === slug);
          if (mock) {
            setProduct({
              ...mock,
              short_description: mock.short_description || '',
              aciklama: mock.short_description || '',
              stock: 99,
              galeri_gorseller: [],
              ozellikler: null,
              para_birimi: 'TRY',
              seo_baslik: null,
              seo_aciklama: null,
              ana_urun_id: null,
              varyantlar: [],
            } as Product);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (slug) fetchProduct();
    return () => { cancelled = true; };
  }, [slug]);

  return { product, loading, error };
}

export function useRelatedProducts(categoryName: string, excludeId: string) {
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    let cancelled = false;

    async function fetch() {
      const { data } = await supabase
        .from('urunler')
        .select('*')
        .eq('site_id', SITE_ID)
        .eq('aktif', true)
        .eq('kategori', categoryName)
        .neq('id', excludeId)
        .limit(4);

      if (cancelled) return;
      if (data && data.length > 0) {
        setProducts(data.map(mapDbProduct));
      }
    }

    if (categoryName && excludeId) fetch();
    return () => { cancelled = true; };
  }, [categoryName, excludeId]);

  return products;
}
